import { Suspense, type JSX } from "react";
import { HorizontalNavigationProps } from "components/horizontal-navigation/horizontal-navigation.props";
import { extractHiddenBlock } from "../../utils/styles";
import HorizontalNavigationToggle from "./HorizontalNavigationToggle";
import Button from "src/atoms/Button";

const DATA_TEMPLATE_ID = "1C82E550EBCD4E5D8ABDD50D0809541E";
const DEV_PLACEHOLDER_TEXT =
  "This is the Horizontal Navigation. Do not delete.";

const HorizontalNavigationContent = ({
  props,
  defaultExpanded,
}: {
  props: HorizontalNavigationProps;
  defaultExpanded: boolean;
}): JSX.Element | null => {
  console.log("---->[HorizontalNavigation] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page?.mode?.isEditing;

  // In preview the item path arrives suffixed with `?sc_site=Dotcom`; strip
  // it so the comparison against `link.path` matches.
  const itemPath = page?.mode?.isPreview
    ? page?.layout?.sitecore?.context?.itemPath?.replace("?sc_site=Dotcom", "")
    : page?.layout?.sitecore?.context?.itemPath;

  const results = fields?.data?.item?.children?.results ?? [];
  const linkResults = results.filter(
    (item) =>
      item.template?.id !== DATA_TEMPLATE_ID && itemPath !== item.link?.path,
  );

  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const containerClass = `@container/nav max-w-[var(--sitewide-container-max-width)] mx-auto ${params?.Styles ?? ""} ${hiddenBlockStyles}`;

  // Empty / current-page-only state: edit-mode shows a dev placeholder so
  // authors don't accidentally delete the rendering. Production renders
  // nothing.
  if (linkResults.length === 0) {
    if (!isPageEditing) return null;

    return (
      <div
        className={containerClass}
        data-role="horizontal-navigation-component"
      >
        <div className="px-4 py-8 @lg/nav:py-16">
          <div className="bg-white border border-lightning-blue rounded-[8px] shadow-[var(--horizontal-nav-shadow)]">
            <div className="flex justify-between cursor-pointer p-4 rounded-[8px]">
              <h4
                className={
                  "m-0 text-dark-black text-base leading-[1.5] font-medium lg:text-[32px] lg:leading-[1.1875] lg:font-normal"
                }
              >
                {DEV_PLACEHOLDER_TEXT}
              </h4>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={null}>
      <HorizontalNavigationToggle
        id={params?.RenderingIdentifier}
        containerClass={containerClass}
        scTitleField={fields?.data?.datasource?.Title?.jsonValue}
        defaultExpanded={defaultExpanded}
      >
        {linkResults.map((item) => (
          <li
            key={item.link.path}
            className="block list-none min-w-[240px] flex-grow-1 flex-shrink-1 @md/nav:basis-[calc(50%_-_12px)] @lg/nav:basis-[calc(33.33%_-_16px)] @md/nav:max-w-[calc(50%_-_12px)] @lg/nav:max-w-[calc(33.33%_-_16px)]"
          >
            <Button
              buttonType="numbers"
              field={{
                value: {
                  href: item?.link?.path,
                  text:
                    item?.navigationTitle?.jsonValue?.value ?? item?.link?.path,
                },
              }}
              iconName="CHEVRON_NUMBER_RIGHT"
              iconColor="text-charcoal-black"
              textColor="text-dark-black"
              className={
                "w-full text-[14px]! leading-[1.42857]! justify-between! border-t-2! pt-[18px]! pb-[18px]! font-medium shadow-none! mb-0!"
              }
              data-role="link"
            />
          </li>
        ))}
      </HorizontalNavigationToggle>
    </Suspense>
  );
};

export const Default = (
  props: HorizontalNavigationProps,
): JSX.Element | null => (
  <HorizontalNavigationContent props={props} defaultExpanded={false} />
);

export const Expanded = (
  props: HorizontalNavigationProps,
): JSX.Element | null => (
  <HorizontalNavigationContent props={props} defaultExpanded={true} />
);
